import { useState, useRef, useEffect } from 'react';

interface PublishStatusBadgeProps {
  lastModifiedAt: string | null;
  lastPublishedAt: string | null;
  onPublish?: () => Promise<void>;
  onDiscard?: () => Promise<void>;
  canDiscard?: boolean;
}

function hasUnpublishedChanges(lastModifiedAt: string | null, lastPublishedAt: string | null): boolean {
  if (!lastModifiedAt) return false;
  if (!lastPublishedAt) return true;
  return new Date(lastModifiedAt) > new Date(lastPublishedAt);
}

export function PublishStatusBadge({ lastModifiedAt, lastPublishedAt, onPublish, onDiscard, canDiscard }: PublishStatusBadgeProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showTooltip) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
        setShowTooltip(false);
        setShowDiscardConfirm(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTooltip]);

  const isDraft = hasUnpublishedChanges(lastModifiedAt, lastPublishedAt);

  const handlePublish = async () => {
    if (!onPublish || isPublishing) return;
    setIsPublishing(true);
    try {
      await onPublish();
      setShowTooltip(false);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleDiscardConfirm = async () => {
    if (!onDiscard || isDiscarding) return;
    setIsDiscarding(true);
    try {
      await onDiscard();
      setShowTooltip(false);
      setShowDiscardConfirm(false);
    } finally {
      setIsDiscarding(false);
    }
  };

  const showDiscardButton = canDiscard && onDiscard;

  return (
    <div className="relative inline-flex items-center" ref={tooltipRef}>
      <button
        onClick={() => isDraft && setShowTooltip(!showTooltip)}
        className={isDraft ? "w-2.5 h-2.5 rounded-full bg-orange-500 animate-pulse cursor-pointer" : "w-2.5 h-2.5"}
        aria-label={isDraft ? "Unpublished changes" : undefined}
      />
      {showTooltip && (
        <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-700 z-50">
          {showDiscardConfirm ? (
            <>
              <p className="font-medium text-red-600 mb-1">Discard all changes?</p>
              <p className="text-xs text-gray-500 mb-2">This will undo all unpublished changes. This cannot be undone.</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowDiscardConfirm(false)}
                  className="flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDiscardConfirm}
                  disabled={isDiscarding}
                  className="flex-1 rounded-md bg-red-500 px-2 py-1.5 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50"
                >
                  {isDiscarding ? "Discarding…" : "Confirm"}
                </button>
              </div>
            </>
          ) : (
            <>
              <p>This site has unpublished changes. Publish to make them live.</p>
              <div className="mt-2 flex gap-2">
                {showDiscardButton && (
                  <button
                    onClick={() => setShowDiscardConfirm(true)}
                    className="flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Discard changes
                  </button>
                )}
                {onPublish && (
                  <button
                    onClick={handlePublish}
                    disabled={isPublishing}
                    className="flex-1 rounded-md bg-orange-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50"
                  >
                    {isPublishing ? "Publishing…" : "Publish"}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
